import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { FeaturedContent } from 'rwl-shared/dist/models';

type ContentListsContextType = {
    jobList: FeaturedContent['jobs'];
    setJobList: (jobList: FeaturedContent['jobs']) => void;
    reviewList: FeaturedContent['reviews'];
    // We track reviews and similar reviews separately to know when we're displaying a recommendation
    // Currently all are thrown into the same bucket in the UI,
    // but we still want to know when we're displaying a recommendation on an individual level
    allReviewsList: FeaturedContent['reviews'];
    setReviewList: (reviewList: FeaturedContent['reviews']) => void;
    similarJobList: FeaturedContent['similarJobs'];
    setSimilarJobList: (jobList: FeaturedContent['similarJobs']) => void;
    similarReviewList: FeaturedContent['similarReviews'];
    setSimilarReviewList: (
        reviewList: FeaturedContent['similarReviews']
    ) => void;
    onJobCardClicked: (jobId: string) => void;
    onReviewCardClicked: (reviewId: string) => void;
    setOnJobCardClickHandler: (
        onJobCardClicked: (jobId: string) => void
    ) => void;
    setOnReviewCardClickHandler: (
        onReviewCardClicked: (reviewId: string) => void
    ) => void;
    totalJobs: number;
    totalReviews: number;
    setTotalJobs: (totalJobs: number) => void;
    setTotalReviews: (totalReviews: number) => void;
    hasMoreJobs: boolean;
    hasMoreReviews: boolean;
    setHasMoreJobs: (hasMoreJobs: boolean) => void;
    setHasMoreReviews: (hasMoreReviews: boolean) => void;
    fetchNextPage: (() => void) | undefined;
    setFetchNextPage: (fetchNextPage: (() => void) | undefined) => void;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
};

const ContentListsContext = createContext<ContentListsContextType | undefined>(
    undefined
);
export const ContentListsProvider = ({
    children,
}: {
    children: ReactNode;
}): JSX.Element => {
    const [jobList, setJobList] = useState<FeaturedContent['jobs']>([]);
    const [reviewList, setReviewList] = useState<FeaturedContent['reviews']>(
        []
    );
    const [allReviewsList, setAllReviewsList] = useState<
        FeaturedContent['reviews']
    >([]);
    const [similarJobList, setSimilarJobList] = useState<
        FeaturedContent['similarJobs']
    >([]);
    const [similarReviewList, setSimilarReviewList] = useState<
        FeaturedContent['similarReviews']
    >([]);
    const [onJobCardClicked, setOnJobCardClickHandlerState] = useState<
        (jobId: string) => void
    >(() => void 0);
    const [onReviewCardClicked, setOnReviewCardClickHandlerState] = useState<
        (reviewId: string) => void
    >(() => void 0);
    const [totalJobs, setTotalJobs] = useState<number>(0);
    const [totalReviews, setTotalReviews] = useState<number>(0);
    const [hasMoreJobs, setHasMoreJobs] = useState<boolean>(false);
    const [hasMoreReviews, setHasMoreReviews] = useState<boolean>(false);
    const [fetchNextPage, setFetchNextPageState] = useState<
        (() => void) | undefined
    >(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setAllReviewsList([...reviewList, ...similarReviewList]);
    }, [reviewList, similarReviewList]);

    return (
        <ContentListsContext.Provider
            value={{
                jobList,
                setJobList,
                reviewList,
                setReviewList,
                allReviewsList,
                similarJobList,
                setSimilarJobList,
                similarReviewList,
                setSimilarReviewList,
                onJobCardClicked,
                onReviewCardClicked,
                setOnJobCardClickHandler: (onJobCardClicked) =>
                    setOnJobCardClickHandlerState(() => onJobCardClicked),
                setOnReviewCardClickHandler: (onReviewCardClicked) =>
                    setOnReviewCardClickHandlerState(() => onReviewCardClicked),
                totalJobs,
                totalReviews,
                setTotalJobs,
                setTotalReviews,
                hasMoreJobs,
                hasMoreReviews,
                setHasMoreJobs,
                setHasMoreReviews,
                fetchNextPage,
                setFetchNextPage: (fetchNextPage) =>
                    setFetchNextPageState(() => fetchNextPage),
                isLoading,
                setIsLoading,
            }}
        >
            {children}
        </ContentListsContext.Provider>
    );
};

export const useContentLists = () => {
    const context = useContext(ContentListsContext);
    if (context === undefined) {
        throw new Error(
            'useContentLists must be used within a ContentListsProvider'
        );
    }
    return context;
};
